import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Download, CheckCircle2, CreditCard, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const PublicInvoice = () => {
  const { invoiceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/invoices/${invoiceId}`);
      setInvoice(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invoice not found or no longer available.');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // Polling after Stripe redirects back with ?paid=1&session_id=
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || polling) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/public/invoices/${invoiceId}/payment-status`, { params: { session_id: sessionId } });
        if (r.data.payment_status === 'paid') {
          await fetchInvoice();
          setPolling(false);
          return;
        }
      } catch { /* keep trying */ }
      if (attempts < 10) {
        setTimeout(tick, 2000);
      } else {
        setPolling(false);
      }
    };
    tick();
  }, [searchParams, invoiceId, fetchInvoice, polling]);

  const payNow = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/invoices/${invoiceId}/pay`);
      window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not start payment. Please try again.');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-md p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-2">Invoice unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';
  const primary = invoice.company?.primary_color || '#002FA7';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-invoice-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Brand header */}
        <header className="mb-6 flex items-center justify-end">
          <a href={`${API}/public/invoices/${invoiceId}/pdf`} target="_blank" rel="noreferrer" data-testid="public-download-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-md px-3 py-1.5">
            <Download className="w-4 h-4" /> PDF
          </a>
        </header>

        {/* Paid banner */}
        {isPaid && (
          <div data-testid="paid-banner" className="mb-4 bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900">Paid — thank you!</p>
              <p className="text-sm text-emerald-800">Your payment was received. A confirmation will reach you shortly.</p>
            </div>
          </div>
        )}

        {/* Invoice card */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {invoice.company?.logo_url && (
            <div className="flex justify-center pt-6 pb-3" data-testid="invoice-card-logo">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${invoice.company.logo_url}`}
                alt={invoice.company?.name}
                className="h-16 w-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="p-6 border-b border-slate-100 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Invoice</p>
              <h2 className="text-2xl font-bold" style={{ color: primary }}>{invoice.invoice_number}</h2>
            </div>
            <div className="text-right text-sm text-slate-600">
              <p>Issued <strong className="text-slate-900">{fmtDate(invoice.issue_date)}</strong></p>
              {invoice.due_date && <p>Due <strong className="text-slate-900">{fmtDate(invoice.due_date)}</strong></p>}
              {invoice.po_number && <p>PO # <strong className="text-slate-900">{invoice.po_number}</strong></p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 border-b border-slate-100 text-sm">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">Bill To</p>
              <p className="font-semibold text-slate-900">{invoice.customer?.name}</p>
              {invoice.customer?.address && <p className="text-slate-600">{invoice.customer.address}</p>}
              {invoice.customer?.email && <p className="text-slate-600">{invoice.customer.email}</p>}
              {invoice.customer?.phone && <p className="text-slate-600">{invoice.customer.phone}</p>}
            </div>
            <div className="text-right sm:text-right">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">From</p>
              <p className="font-semibold text-slate-900">{invoice.company?.name}</p>
              {invoice.company?.address && <p className="text-slate-600 whitespace-pre-line">{invoice.company.address}</p>}
              {invoice.company?.email && <p className="text-slate-600">{invoice.company.email}</p>}
              {invoice.company?.phone && <p className="text-slate-600">{invoice.company.phone}</p>}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Description</th>
                  <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Qty</th>
                  <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Rate</th>
                  <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((li, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-3 px-4 text-slate-900">{li.description}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{li.quantity}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{fmtUSD(li.rate)}</td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-900">{fmtUSD(li.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-6 border-t border-slate-100 flex justify-end">
            <div className="w-full sm:w-72 text-sm space-y-1">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-semibold text-slate-900">{fmtUSD(invoice.subtotal)}</span></div>
              {invoice.tax_rate > 0 && (
                <div className="flex justify-between text-slate-600"><span>Tax ({invoice.tax_rate}%)</span><span className="font-semibold text-slate-900">{fmtUSD(invoice.tax_amount)}</span></div>
              )}
              <div className="flex justify-between text-lg pt-2 border-t border-slate-100 mt-2">
                <span className="font-bold" style={{ color: primary }}>Total</span>
                <span data-testid="invoice-total" className="font-bold" style={{ color: primary }}>{fmtUSD(invoice.total)}</span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="px-6 pb-6 text-sm text-slate-600">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">Notes</p>
              <div className="whitespace-pre-line leading-relaxed" data-testid="public-invoice-notes">{invoice.notes}</div>
            </div>
          )}
        </div>

        {/* Pay button */}
        {!isPaid && (
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-end">
            {polling && (
              <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Confirming payment…</p>
            )}
            <button
              data-testid="pay-now"
              onClick={payNow}
              disabled={paying}
              className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md shadow-sm disabled:opacity-50 transition-colors"
              style={{ background: primary }}
            >
              {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
              {paying ? 'Redirecting to Stripe…' : `Pay ${fmtUSD(invoice.total)}`}
            </button>
          </div>
        )}

        <p className="text-center text-[11px] text-slate-400 mt-8">Powered by CivilX TMS · Secure checkout by Stripe</p>
      </div>
    </div>
  );
};

export default PublicInvoice;
